import React, { useState, useEffect, useCallback } from "react";
import {
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
} from "react-icons/fi";

const Lightbox = ({ images = [], currentIndex = 0, isOpen, onClose }) => {
  const [index, setIndex] = useState(currentIndex);

  useEffect(() => {
    setIndex(currentIndex);
  }, [currentIndex]);

  const goNext = useCallback(() => {
    setIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };

    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, goNext, goPrev]);

  if (!isOpen || images.length === 0) return null;

  const current = images[index];
  const src = typeof current === "string" ? current : current?.url || current?.src;
  const name = typeof current === "string" ? "" : current?.name;

  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center"
      style={{ zIndex: 9999999 }}
      onClick={onClose}
    >
      {/* Download */}
      {src && (
        <a
          href={src}
          download={name || undefined}
          target="_blank"
          rel="noreferrer"
          aria-label="Download image"
          onClick={(e) => e.stopPropagation()}
          className="absolute top-4 right-14 text-white/70 hover:text-white transition-colors"
        >
          <FiDownload size={22} />
        </a>
      )}

      {/* Close */}
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
      >
        <FiX size={24} />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/60 text-sm font-medium">
        {index + 1} / {images.length}
      </div>

      {/* Filename */}
      {name && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-xs truncate max-w-[80%] text-center">
          {name}
        </div>
      )}

      {/* Prev */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-all"
        >
          <FiChevronLeft size={22} />
        </button>
      )}

      {/* Image */}
      <img
        src={src}
        alt={name || `Image ${index + 1}`}
        className="max-h-[85vh] max-w-[90vw] object-contain select-none"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />

      {/* Next */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-all"
        >
          <FiChevronRight size={22} />
        </button>
      )}
    </div>
  );
};

export default Lightbox;
